// ============================================
// REGISTER PAGE - COMPANY PANEL + i18n
// ============================================

import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth';
import { authApi } from '@/services/api';
import { useEffect, useState } from 'react';
import { useGoogleLogin } from '@/hooks';
import toast from 'react-hot-toast';
import { countries } from '@/utils';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/register')({
  component: RegisterPage,
});

const registerSchema = z.object({
  name: z.string().min(2, 'Şirket adı en az 2 karakter olmalıdır').max(100, 'Şirket adı en fazla 100 karakter olabilir'),
  email: z.string().email('Geçerli bir e-posta adresi giriniz'),
  country: z.string().min(2, 'Ülke seçiniz'),
  city: z.string().optional(),
  password: z.string()
    .min(8, 'Şifre en az 8 karakter olmalıdır')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Şifre en az bir büyük harf, bir küçük harf ve bir rakam içermelidir'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Şifreler eşleşmiyor',
  path: ['confirmPassword'],
}).and(
  z.object({
    acceptTerms: z.literal(true, {
      errorMap: () => ({ message: 'Sözleşmeleri kabul etmelisiniz.' }),
    }),
  })
);

type RegisterFormData = z.infer<typeof registerSchema>;

function RegisterPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { isAuthenticated, login } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const googleLoginMutation = useGoogleLogin();

  const handleGoogleCallback = (response: any) => {
    googleLoginMutation.mutate({ credential: response.credential, role: 'COMPANY' });
  };

  useEffect(() => {
    let checkGoogle: any;
    
    const initializeGoogleSignIn = () => {
      if (typeof window !== 'undefined' && window.google) {
        window.google.accounts.id.initialize({
          client_id: '814477697426-tl8102p2a1hcqtglaikrkkvfrn5gpago.apps.googleusercontent.com',
          callback: handleGoogleCallback,
        });
        window.google.accounts.id.renderButton(
          document.getElementById('googleBtnRegister'),
          { theme: 'outline', size: 'large', width: '100%' }
        );
      }
    };

    if (window.google) {
      initializeGoogleSignIn();
    } else {
      checkGoogle = setInterval(() => {
        if (window.google) {
          initializeGoogleSignIn();
          clearInterval(checkGoogle);
        }
      }, 500);
    }

    return () => {
      if (checkGoogle) clearInterval(checkGoogle);
    };
  }, []);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: { country: 'TR', acceptTerms: undefined },
  });

  const registerMutation = useMutation({
    mutationFn: (data: Omit<RegisterFormData, 'confirmPassword'>) => 
      authApi.registerCompany(data as any),
    onSuccess: (response: any) => {
      const result = response.data || response;
      const { user, accessToken, refreshToken } = result;
      
      if (user) {
        toast.success('Kayıt başarılı! Lütfen giriş yapmadan önce e-posta adresinize gönderilen doğrulama linkine tıklayın.', { duration: 6000 });
        navigate({ to: '/login' });
      }
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message || t('register.errorDefault');
      if (
        message.toLowerCase().includes('email') && 
        (message.toLowerCase().includes('exist') || message.toLowerCase().includes('already registered'))
      ) {
        setError('email', { type: 'manual', message: 'Bu e-posta adresi zaten kayıtlı.' });
      } else {
        toast.error(message);
      }
    },
  });

  useEffect(() => {
    if (isAuthenticated) {
      window.location.href = '/';
    }
  }, [isAuthenticated]);

  const onSubmit = (data: RegisterFormData) => {
    const { confirmPassword, ...registerData } = data;
    registerMutation.mutate(registerData);
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 py-8 relative"
      style={{
        backgroundImage: `url('/login-bg.webp')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Mavi overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-maritime-900/85 to-maritime-700/80" />
      
      <div className="w-full max-w-xl relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm mb-4">
            <img src="/favicon.svg" alt="Crew At Sea" className="w-10 h-10 brightness-0 invert" />
          </div>
          <h1 className="text-3xl font-bold text-white">Crew At Sea</h1>
          <p className="text-maritime-200 mt-1">{t('panelRegisterSubtitle.company')}</p>
        </div>

        {/* Kayıt Formu */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">{t('register.title')}</h2>

          {/* Hata Mesajı */}
          {registerMutation.isError && !errors.email && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                {(() => {
                  const err: any = registerMutation.error;
                  const msg = err?.response?.data?.message || err?.message || '';
                  return msg || t('register.errorDefault');
                })()}
              </span>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
            {/* Şirket Adı */}
            <div>
              <label htmlFor="name" className="label">{t('register.companyName')}</label>
              <input 
                id="name" 
                type="text" 
                className={`input ${errors.name ? 'input-error' : ''}`} 
                placeholder={t('register.companyNamePlaceholder')} 
                {...register('name')} 
              />
              {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>}
            </div>

            {/* E-posta */}
            <div>
              <label htmlFor="email" className="label">{t('register.email')}</label>
              <input 
                id="email" 
                type="email" 
                className={`input ${errors.email ? 'input-error' : ''}`} 
                placeholder="sirket@example.com" 
                {...register('email')} 
              />
              {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
            </div>

            {/* Ülke, Şehir */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="country" className="label">{t('register.country')}</label>
                <select id="country" className={`input ${errors.country ? 'input-error' : ''}`} {...register('country')}>
                  <option value="">{t('register.select')}</option>
                  {countries.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
                {errors.country && <p className="mt-1 text-xs text-red-500">{errors.country.message}</p>}
              </div>
              <div>
                <label htmlFor="city" className="label">{t('register.cityOptional')}</label>
                <input 
                  id="city" 
                  type="text" 
                  className={`input ${errors.city ? 'input-error' : ''}`} 
                  placeholder={t('register.cityPlaceholder')} 
                  {...register('city')} 
                />
                {errors.city && <p className="mt-1 text-xs text-red-500">{errors.city.message}</p>}
              </div>
            </div>

            {/* Şifre, Şifre Tekrar */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="password" className="label">{t('register.password')}</label>
                <div className="relative">
                  <input 
                    id="password" 
                    type={showPassword ? 'text' : 'password'} 
                    className={`input pr-10 ${errors.password ? 'input-error' : ''}`} 
                    placeholder="••••••••" 
                    {...register('password')} 
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password.message}</p>}
              </div>
              <div>
                <label htmlFor="confirmPassword" className="label">{t('register.confirmPassword')}</label>
                <div className="relative">
                  <input 
                    id="confirmPassword" 
                    type={showConfirmPassword ? 'text' : 'password'} 
                    className={`input pr-10 ${errors.confirmPassword ? 'input-error' : ''}`} 
                    placeholder="••••••••" 
                    {...register('confirmPassword')} 
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="mt-1 text-xs text-red-500">{errors.confirmPassword.message}</p>}
              </div>
            </div>

            {/* Şifre ipucu */}
            <p className="text-xs text-gray-500">
              {t('register.passwordHint')}
            </p>

            {/* Sözleşme Onay */}
            <div className="flex items-start gap-2 mt-4">
              <input
                id="acceptTerms"
                type="checkbox"
                className={`mt-1 h-4 w-4 rounded border-gray-300 text-maritime-600 focus:ring-maritime-500 ${errors.acceptTerms ? 'border-red-500' : ''}`}
                {...register('acceptTerms')}
              />
              <label htmlFor="acceptTerms" className="text-xs text-gray-600 leading-tight">
                <a href={import.meta.env.VITE_FRONTEND_URL ? `${import.meta.env.VITE_FRONTEND_URL}/tr/terms` : 'https://crewatsea.com/tr/terms'} target="_blank" rel="noopener noreferrer" className="text-maritime-600 hover:underline">Şirket Kullanıcı Sözleşmesini</a>,{' '}
                <a href={import.meta.env.VITE_FRONTEND_URL ? `${import.meta.env.VITE_FRONTEND_URL}/tr/privacy` : 'https://crewatsea.com/tr/privacy'} target="_blank" rel="noopener noreferrer" className="text-maritime-600 hover:underline">Gizlilik Politikasını</a> ve{' '}
                <a href={import.meta.env.VITE_FRONTEND_URL ? `${import.meta.env.VITE_FRONTEND_URL}/tr/kvkk` : 'https://crewatsea.com/tr/kvkk'} target="_blank" rel="noopener noreferrer" className="text-maritime-600 hover:underline">KVKK Aydınlatma Metnini</a> okudum ve kabul ediyorum.
              </label>
            </div>
            {errors.acceptTerms && <p className="mt-1 text-xs text-red-500">{errors.acceptTerms.message}</p>}

            {/* Gönder Butonu */}
            <button
              type="submit"
              className="btn-primary w-full mt-4"
              disabled={registerMutation.isPending}
            >
              {registerMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('register.submitting')}
                </>
              ) : (
                t('register.submit')
              )}
            </button>
          </form>

          {/* Google ile Devam Et */}
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-2 bg-white text-gray-400">veya</span>
            </div>
          </div>

          <div className="w-full flex justify-center min-h-[44px]">
            {googleLoginMutation.isPending ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin text-maritime-600" />
                Giriş yapılıyor...
              </div>
            ) : (
              <>
                <style>{`
                  #googleBtnRegister {
                    display: flex !important;
                    justify-content: center !important;
                    width: 100% !important;
                  }
                  #googleBtnRegister > div {
                    margin: 0 auto !important;
                  }
                `}</style>
                <div id="googleBtnRegister" className="w-full"></div>
              </>
            )}
          </div>

          {/* Giriş Linki */}
          <p className="mt-6 text-center text-sm text-gray-500">
            {t('register.hasAccount')}{' '}
            <Link to="/login" className="text-maritime-600 hover:text-maritime-700 font-medium">
              {t('register.loginLink')}
            </Link>
          </p>
        </div>

        {/* Footer */}
        <p className="text-center text-maritime-200 text-sm mt-8">
          {t('footer.copyright', { year: 2025 })}
        </p>
      </div>
    </div>
  );
}
